import React, { useMemo } from "react";
import "../Terms/Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const ShippingPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+1 (000) 000-0000",
      phonecall: webinfo.phonecall || "+10000000000",
      address: webinfo.address || "United States",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="terms">
      <div className="terms-wrap">
        <header className="terms-head">
          <span className="terms-kicker">Legal</span>
          <h1>Shipping &amp; Delivery Policy</h1>
          <p className="terms-meta">
            <span>Provider: {brand.name}</span>
            <span>Last updated: {effectiveDate}</span>
            <span>Applicable to: United States</span>
          </p>
        </header>

        <div className="terms-content">
          <TermsBlock title="1. Overview">
            <p>
              This policy describes how {brand.name} delivers services and any physical goods to clients. We operate in the United States and serve clients nationally and, where agreed, internationally. Delivery terms may be further specified in your project agreement or statement of work.
            </p>
          </TermsBlock>

          <TermsBlock title="2. Digital deliverables">
            <p>
              Most of our work is delivered digitally. This includes but is not limited to:
            </p>
            <ul>
              <li>Design files, source code, and project assets (via secure link, cloud storage, or repository access)</li>
              <li>Documentation, reports, and strategy materials</li>
              <li>Access to hosted environments, dashboards, or accounts we configure for you</li>
            </ul>
            <p>
              Delivery timelines for digital deliverables are set in your proposal or contract. We will provide access or download links by the agreed date unless otherwise communicated. You are responsible for downloading or saving deliverables; we typically retain access for a limited period (e.g., 30–90 days) unless otherwise agreed.
            </p>
          </TermsBlock>

          <TermsBlock title="3. Physical goods (if applicable)">
            <p>
              If we ship physical items (e.g., branded materials, hardware, or merchandise) within the United States, the following apply:
            </p>
            <ul>
              <li><strong>Shipping methods:</strong> We use standard carriers (e.g., USPS, FedEx, UPS) and select service levels based on your order and timeline.</li>
              <li><strong>Shipping times:</strong> Domestic U.S. delivery typically takes 3–7 business days for standard shipping, unless expedited shipping is selected and paid for.</li>
              <li><strong>Rates:</strong> Shipping costs will be quoted at checkout or in your invoice. Free shipping, if offered, applies only to the contiguous United States unless stated otherwise.</li>
              <li><strong>Tracking:</strong> When available, we will provide a tracking number so you can monitor your shipment.</li>
            </ul>
          </TermsBlock>

          <TermsBlock title="4. Alaska, Hawaii, and U.S. territories">
            <p>
              Orders to Alaska, Hawaii, and U.S. territories may incur additional shipping time and cost. We will communicate any surcharges or extended delivery estimates before you confirm the order.
            </p>
          </TermsBlock>

          <TermsBlock title="5. International delivery">
            <p>
              For international clients or shipments outside the United States, delivery terms, customs, and any duties or taxes are the responsibility of the recipient unless otherwise agreed in writing. Delivery times vary by destination and carrier.
            </p>
          </TermsBlock>

          <TermsBlock title="6. Delays and force majeure">
            <p>
              We are not responsible for delays caused by carriers, customs, weather, or events beyond our reasonable control. If a delay occurs, we will work with you to adjust timelines or options where feasible.
            </p>
          </TermsBlock>

          <TermsBlock title="7. Lost or damaged shipments">
            <p>
              If a physical shipment is lost or arrives damaged, please contact us within 7 days of the estimated delivery date. We will work with the carrier to file a claim and, where appropriate, replace or refund in accordance with our Refund Policy and your agreement.
            </p>
          </TermsBlock>

          <TermsBlock title="8. Changes to this policy">
            <p>
              We may update this Shipping &amp; Delivery Policy from time to time. The “Last updated” date at the top reflects the most recent version. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </TermsBlock>

          <TermsBlock title="9. Contact">
            <ul className="terms-contact">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Address:</strong> {brand.address}
              </li>
            </ul>
          </TermsBlock>
        </div>
      </div>
    </section>
  );
};

const TermsBlock = ({ title, children }) => (
  <section className="terms-card">
    <h2>{title}</h2>
    {children}
  </section>
);

export default ShippingPolicy;
